import { patchScalarGates } from './ScalarGatePatch';
import { canSeparateDecoration } from './DecorativeTopologyPolicy';
import {
  compatibleBundleConnections,
  BUNDLE_COMPATIBILITY_MESSAGE,
  BUNDLE_COMPATIBILITY_BUDGET_MESSAGE,
} from './BundleCompatibility';
import { ownVisualAssignment } from '@/utils/OwnedBundleVisualData';
import { captureNodeRuntimeState, hasNativeRuntimeIdentity } from './LogicNodeInstance';
import { standaloneRuntimeIds, type RuntimeGraphIds } from './RuntimeGraphIds';
import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import {
  ensureBundleSignalIdentities,
  containsChipMemberIdentity,
} from './BundleSignalIdentity';
import {
  captureTopology,
  captureTopologyPatch,
  isPatchedTopology,
  type GraphEditPatch,
  changedTopology,
  chipVertex,
  type TopologySnapshot,
} from './GraphTopology';
import { LogicGraph } from './LogicGraph';
import {
  ITileManagerModel,
  IConnectionModel,
  IChipInstanceModel,
} from '@/interfaces/models';
import { TileType } from '@/models/TileTypes';
import { TileInstance } from '@/models/tiles/TileInstance';
import { tileDefinitionRegistry } from '../tiles/TileRegistry';
import { GraphBuildQueueItem } from '@/models/tiles/TileDefinition';
import { CircuitType, Side } from '@/schemas/common';
import { InterfaceTileState } from '@/schemas/tiles';
import { InterfaceLinkInstanceId } from '@/types/ids';
import { Direction } from '@/utils/direction';
import { InterfaceType } from '@/models/ChipModel';
import {
  restoreInstanceRuntime,
  cloneLiftedInstance,
  validateChipLift,
} from './restoreInstanceRuntime';
import { TileSideIOType } from '@/schemas/tiles';
import { migrateAdapterCables } from './migrateAdapterCables';
import { logicNodeRegistry } from './LogicNodeRegistry';
import { buildCountFreeCableLanes } from './CountFreeCableGraph';
import { buildBundleLanes, refreshBundleSignalCatalog } from './BundleGraphBuilder';
import { assignBundleVisualOrder } from './BundleVisualOrder';
import {
  $mobx,
  _getAdministration,
  _getGlobalState,
  _isComputingDerivation,
  isObservableArray,
} from 'mobx';

const nativeLaneSearch = Array.prototype.indexOf;
interface LaneReadAdministration {
  getArrayLength_(): number;
  get_(index: number): NodeHandle;
}
type NativeLaneTarget = NodeHandle[] & { [$mobx]: LaneReadAdministration };

function unobservedLaneTarget(lanes: NodeHandle[]): NativeLaneTarget | undefined {
  if (
    _isComputingDerivation() ||
    _getGlobalState().spyListeners.length ||
    !isObservableArray(lanes)
  )
    return undefined;
  const admin = _getAdministration(lanes);
  if (
    !(
      admin.proxy_ === lanes &&
      admin.dehancer === undefined &&
      !admin.interceptors_?.length &&
      !admin.changeListeners_?.length &&
      admin.atom_?.observers_?.size === 0 &&
      Array.isArray(admin.values_) &&
      Object.getPrototypeOf(admin.values_) === Array.prototype &&
      !Object.hasOwn(admin.values_, 'indexOf') &&
      Array.prototype.indexOf === nativeLaneSearch
    )
  )
    return undefined;
  // The non-legacy array proxy dispatches length and numeric reads through
  // this target's administration. Keep that dispatch (including live read hooks
  // and dynamic lengths), without crossing the proxy for every lane.
  if (
    admin.legacyMode_ !== false ||
    Object.getOwnPropertyDescriptor(admin.values_, $mobx)?.value !== admin
  )
    return undefined;
  return admin.values_ as NativeLaneTarget;
}

export class GraphManager {
  private rebuildIndexDelta?: {
    previousGraph: LogicGraph;
    graph: LogicGraph;
    tileIds: ReadonlySet<string>;
    previousNodeIds: ReadonlySet<NodeHandle>;
    tiles: TileInstance[];
    nativePatch: boolean;
  };

  /** A partial graph build already knows which tile/node bindings it replaced. */
  takeRebuildIndexDelta(previousGraph: LogicGraph) {
    const delta = this.rebuildIndexDelta;
    this.rebuildIndexDelta = undefined;
    return delta?.previousGraph === previousGraph && delta.graph === this.graph
      ? delta
      : undefined;
  }

  #nativeSnapshotModel = undefined;
  /** Brand check rejects opaque proxies before inspecting any snapshot hooks. */
  static isNativeSnapshotModel(value: unknown): value is GraphManager {
    return (
      !!value &&
      typeof value === 'object' &&
      #nativeSnapshotModel in value &&
      Object.getPrototypeOf(value) === GraphManager.prototype
    );
  }

  graph: LogicGraph;
  private nodeAliases = new Map<NodeHandle, NodeHandle>();

  /** Resolve wire IDs removed while collapsing nets at chip boundaries. */
  resolveNodeId(id: NodeHandle): NodeHandle {
    let next = this.nodeAliases.get(id);
    while (next) {
      id = next;
      next = this.nodeAliases.get(id);
    }
    return id;
  }

  private topology?: TopologySnapshot;
  private snapshotGraph?: LogicGraph;
  private nodeTiles = new Map<NodeHandle, Set<TileInstance>>();
  private wirelessHubs = new Map<string, { nodeId: NodeHandle; tileIds: string[] }>();
  tileManager: ITileManagerModel;
  connectionModel?: IConnectionModel;
  private buildConnections?: IConnectionModel;
  private previousIdentityMode?: boolean;

  private checkIdentityMode() {
    const mode =
      this.tileManager.tiles[0]?.context?.userPreferences?.bundleChipIdentityEnabled ===
      true;
    if (mode !== this.previousIdentityMode) this.topology = undefined;
    this.previousIdentityMode = mode;
    return mode;
  }
  chipInstances: IChipInstanceModel[];

  constructor(
    tileManager: ITileManagerModel,
    connectionModel?: IConnectionModel,
    chipInstances?: IChipInstanceModel[],
    private readonly ids: RuntimeGraphIds = standaloneRuntimeIds,
  ) {
    this.tileManager = tileManager;
    this.connectionModel = connectionModel;
    this.chipInstances = chipInstances ?? [];
    this.graph = new LogicGraph(this.ids);
  }

  rebuildGraph(patch?: GraphEditPatch) {
    this.rebuildIndexDelta = undefined;
    this.checkIdentityMode();
    ensureBundleSignalIdentities(this.tileManager.tiles);
    let certifyDecoration = false;
    // Initial/full replacement builds have no prior topology to compare.
    if (!this.topology || this.snapshotGraph !== this.graph) {
      certifyDecoration = canSeparateDecoration(
        this.tileManager,
        undefined,
        this.connectionModel,
      );
      this.rebuildAll();
    } else {
      const next = patch
        ? captureTopologyPatch(
            this.topology,
            this.tileManager,
            this.connectionModel,
            this.chipInstances,
            patch,
          )
        : captureTopology(this.tileManager, this.connectionModel, this.chipInstances);
      const gatePatch = patchScalarGates(
        this.topology,
        next,
        this.graph,
        this.tileManager,
        this.connectionModel,
      );
      if (gatePatch) {
        const previousGraph = this.graph;
        this.graph = gatePatch.graph;
        const subset = new Proxy(this.tileManager, {
          get: (target, key) =>
            key === 'tiles' ? gatePatch.tiles : Reflect.get(target, key, target),
        });
        for (const [id, entry] of captureTopology(
          subset,
          this.connectionModel,
          [],
          false,
          this.tileManager,
        ))
          next.set(id, entry);
        this.topology = next;
        this.snapshotGraph = this.graph;
        this.rebuildIndexDelta = { ...gatePatch, previousGraph, nativePatch: true };
        return;
      }
      const affected = changedTopology(this.topology, next);
      if (affected.size === 0) return;
      const removedNodes = new Set<NodeHandle>();
      for (const id of affected)
        for (const nodeId of this.topology.get(id)?.nodeIds ?? [])
          removedNodes.add(nodeId);
      for (const [channel, hub] of this.wirelessHubs) {
        if (hub.tileIds.some((id) => affected.has(id))) {
          removedNodes.add(hub.nodeId);
          this.wirelessHubs.delete(channel);
        }
      }
      const tiles = this.tileManager.tiles.filter((tile) => affected.has(tile.id));
      const tileIds = new Set(tiles.map((tile) => tile.id));
      // Restrict the builder's traversal and wire-merge scans to changed components.
      const manager = new Proxy({} as ITileManagerModel, {
        get: (_target, property) => {
          const target = this.tileManager;
          if (property === 'tiles') return tiles;
          if (property === 'get')
            return (point: import('@/interfaces/models').IPoint) => {
              const tile = target.get(point);
              return tile && tileIds.has(tile.id) ? tile : undefined;
            };
          return Reflect.get(target, property, target);
        },
      });
      const partial = new GraphManager(
        manager,
        this.connectionModel,
        this.chipInstances.filter((chip) => affected.has(chipVertex(chip.id))),
        this.ids,
      );
      partial.graph = this.graph;
      partial.rebuildAll();
      const decorationStillNative =
        tiles.every((tile) => next.get(tile.id)?.nativeDecoration === true) &&
        canSeparateDecoration(this.tileManager, tiles, this.connectionModel);
      if (!decorationStillNative)
        for (const id of affected) {
          const entry = next.get(id);
          if (entry) entry.nativeDecoration = false;
        }
      const merged = new LogicGraph(this.ids);
      for (const [id, node] of this.graph.nodes)
        if (!removedNodes.has(id)) merged.nodes.set(id, node);
      for (const edge of this.graph.edges.values()) {
        if (!removedNodes.has(edge.sourceNodeId) && !removedNodes.has(edge.targetNodeId))
          merged.addEdge(edge.sourceNodeId, edge.targetNodeId, edge.meta);
      }
      merged.mergeFrom(partial.graph);
      const staleAliases = [...this.nodeAliases.keys()].filter((id) =>
        removedNodes.has(this.resolveNodeId(id)),
      );
      for (const id of staleAliases) this.nodeAliases.delete(id);
      for (const [id, target] of partial.nodeAliases) this.nodeAliases.set(id, target);
      for (const [channel, hub] of partial.wirelessHubs)
        this.wirelessHubs.set(channel, hub);
      const previousGraph = this.graph;
      this.graph = merged;
      // The pre-build snapshot already covers unchanged components. Only rebuilt
      // tiles/chips can have new node IDs or build-generated configuration.
      // Recapturing the whole circuit here made a one-tile edit pay for two scans.
      for (const [id, entry] of captureTopology(
        manager,
        this.connectionModel,
        partial.chipInstances,
      )) {
        const beforeBuild = next.get(id);
        entry.nativeDecoration = beforeBuild?.nativeDecoration;
        // Partial managers cannot see geometric neighbors outside the rebuilt
        // component. Keep those conservative edges for future custom builds.
        for (const neighbor of beforeBuild?.neighbors ?? [])
          if (!affected.has(neighbor) && !entry.neighbors.has(neighbor)) {
            entry.neighbors.add(neighbor);
            if (beforeBuild?.decorationEdges?.has(neighbor))
              (entry.decorationEdges ??= new Set()).add(neighbor);
          }
        next.set(id, entry);
      }
      this.topology = next;
      this.snapshotGraph = this.graph;
      this.rebuildIndexDelta = {
        previousGraph,
        graph: this.graph,
        tileIds: affected,
        previousNodeIds: removedNodes,
        tiles,
        nativePatch: isPatchedTopology(next),
      };
      return;
    }
    this.topology = captureTopology(
      this.tileManager,
      this.connectionModel,
      this.chipInstances,
      certifyDecoration,
    );
    this.snapshotGraph = this.graph;
  }

  /** Cooperative initial build used while hydrating large saved chips. */
  *rebuildGraphSteps(): Generator<void> {
    this.rebuildIndexDelta = undefined;
    this.checkIdentityMode();
    ensureBundleSignalIdentities(this.tileManager.tiles);
    if (this.topology && this.snapshotGraph === this.graph) {
      this.rebuildGraph();
      return;
    }
    yield* this.rebuildAllSteps();
    this.topology = captureTopology(
      this.tileManager,
      this.connectionModel,
      this.chipInstances,
      false,
    );
    this.snapshotGraph = this.graph;
  }

  private rebuildAll() {
    const steps = this.rebuildAllSteps();
    while (!steps.next().done) {
      /* synchronous editor updates keep their existing behavior */
    }
  }

  private *rebuildAllSteps(): Generator<void> {
    let work = 0;
    const tiles = this.tileManager.tiles;
    const previousLanes = new Map(
      [...this.graph.nodes.values()]
        .filter((node) => node.rootRuntimeKey?.startsWith('["bundle",'))
        .map((node) => [node.rootRuntimeKey!, captureNodeRuntimeState(node)]),
    );
    // Capture state from the old graph
    const tileStateMap = new Map<string, Partial<Record<Side, any>>>();
    for (const tile of tiles) {
      if (++work % 256 === 0) yield;
      if (tile.meta.partOfChip) continue;
      const definition = tileDefinitionRegistry.get(tile.tileType);
      if (definition.saveState) {
        tileStateMap.set(
          tile.id,
          definition.saveState({ instance: tile, graph: this.graph }),
        );
      } else if (tile.logicNodeIds) {
        const savedState: Partial<Record<Side, any>> = {};
        for (const [side, nodeId] of Object.entries(tile.logicNodeIds)) {
          const node = this.graph.nodes.get(nodeId);
          if (node) {
            // Preserve copied output/type and the existing state alias across yields.
            savedState[side as Side] = captureNodeRuntimeState(node);
          }
        }
        tileStateMap.set(tile.id, savedState);
      }
    }

    this.graph = new LogicGraph(this.ids);
    this.nodeAliases.clear();

    const adapterOwners = new Set(
      this.chipInstances
        .filter((instance) => instance.chip?.kind === 'bundle-adapter')
        .map((instance) => instance.id),
    );
    for (const tile of tiles) {
      tile.adapterPin =
        tile.tileType === TileType.SingleInterface &&
        !!tile.meta.partOfChip &&
        adapterOwners.has(tile.meta.partOfChip as import('@/types/ids').ChipInstanceId);
      if (tile.tileType === TileType.Wire) delete tile.meta.cable;
      tile.logicNodeIds = undefined;
      tile.laneNodeIds = undefined;
      tile.laneSignalIds = undefined;
      tile.laneSignalLabels = undefined;
      tile.bundleSignalLabels = undefined;
      tile.laneVisualFlips = undefined;
      tile.bundleErrors = ownVisualAssignment(tile, 'bundleErrors', []);
      if (++work % 256 === 0) yield;
    }

    const strict = this.checkIdentityMode();
    this.buildConnections = this.connectionModel;
    if (strict && this.connectionModel) {
      const compatibility = compatibleBundleConnections(
        this.tileManager,
        this.connectionModel,
        this.chipInstances,
      );
      this.buildConnections = compatibility.view;
      const blockedTiles = new Set(
        compatibility.blocked.flatMap((edge) => [edge.a.tileId, edge.b.tileId]),
      );
      for (const tile of tiles)
        if (blockedTiles.has(tile.id))
          tile.bundleErrors.push(
            compatibility.budgetExceeded
              ? BUNDLE_COMPATIBILITY_BUDGET_MESSAGE
              : BUNDLE_COMPATIBILITY_MESSAGE,
          );
    }
    if (this.connectionModel && !strict)
      migrateAdapterCables(this.tileManager, this.connectionModel, this.chipInstances);
    for (const tile of tiles) if (tile.tileType === TileType.Wire) delete tile.meta.cable;

    // Migration can clear bundleWidth on passive chips. Capture owners afterward
    // so traversal and embedding use the same mode. One Set avoids scanning all
    // instances during every tile visit.
    const liftedOwners = new Set(
      this.chipInstances
        .filter((instance) => instance.bundleWidth)
        .map((instance) => instance.id),
    );
    // BFS using index pointer instead of queue.shift() — O(T) instead of O(T²)
    const queue: GraphBuildQueueItem[] = [];
    let queueHead = 0;
    const visited = new Set<string>();

    for (const tile of this.tileManager.tiles) {
      if (++work % 128 === 0) yield;
      if (visited.has(tile.id)) continue;

      // Start traversal from this tile (as a root or disconnected component)
      this.processTile(liftedOwners, tile, queue, visited);

      while (queueHead < queue.length) {
        const item = queue[queueHead++];
        this.processTile(
          liftedOwners,
          item.tile,
          queue,
          visited,
          item.sourceNodeId,
          item.sourceTileId,
          item.entryDirection,
        );
        if (++work % 128 === 0) yield;
      }
    }

    this.processChipInstances();
    this.connectWirelessChannels();
    buildCountFreeCableLanes(this.graph, this.tileManager, this.buildConnections);
    buildBundleLanes(this.graph, this.tileManager, this.buildConnections);
    this.nodeTiles.clear();
    for (const tile of this.tileManager.tiles) {
      for (const nodeId of [
        ...Object.values(tile.logicNodeIds ?? {}),
        ...Object.values(tile.laneNodeIds ?? {}).flat(),
      ]) {
        const refs = this.nodeTiles.get(nodeId) ?? new Set<TileInstance>();
        refs.add(tile);
        this.nodeTiles.set(nodeId, refs);
      }
    }
    this.collapseWireToWireEdges();
    refreshBundleSignalCatalog(this.graph, this.tileManager.tiles);
    assignBundleVisualOrder(this.tileManager, this.buildConnections);
    this.nodeTiles.clear();
    for (const node of this.graph.nodes.values()) {
      const previous = node.rootRuntimeKey && previousLanes.get(node.rootRuntimeKey);
      if (previous) node.restoreRuntimeState(previous);
    }

    // A tile may expose the same node on several sides. Choose a canonical key
    // independent of traversal order; nested nodes already carry instance paths.
    const keys = new Map<NodeHandle, string>();
    for (const tile of this.tileManager.tiles) {
      if (++work % 256 === 0) yield;
      if (tile.meta.partOfChip) continue;
      for (const [side, nodeId] of Object.entries(tile.logicNodeIds ?? {})) {
        const key = JSON.stringify(['tile', tile.id, side]);
        if (!keys.has(nodeId) || key < keys.get(nodeId)!) keys.set(nodeId, key);
      }
    }
    for (const [nodeId, key] of keys) {
      const node = this.graph.nodes.get(nodeId);
      if (node) node.runtimeKey = key;
    }

    // Restore state to the new graph
    for (const tile of this.tileManager.tiles) {
      if (++work % 256 === 0) yield;
      const savedState = tileStateMap.get(tile.id);
      if (!savedState) continue;

      const definition = tileDefinitionRegistry.get(tile.tileType);
      if (definition.restoreState) {
        definition.restoreState({ instance: tile, graph: this.graph, savedState });
      } else if (tile.logicNodeIds) {
        for (const [side, nodeId] of Object.entries(tile.logicNodeIds)) {
          const saved = savedState[side as Side];
          if (saved === undefined) continue;
          const newNode = this.graph.nodes.get(nodeId);
          newNode?.restoreRuntimeState(saved);
        }
      }
    }
  }

  private connectWirelessChannels() {
    this.wirelessHubs.clear();
    const channels = new Map<
      string,
      { send: NodeHandle[]; receive: NodeHandle[]; tileIds: string[] }
    >();
    for (const tile of this.tileManager.tiles) {
      if (tile.meta.partOfChip) continue;
      const membership = tileDefinitionRegistry
        .get(tile.tileType)
        .wirelessChannel?.(tile.state);
      const nodeId = tile.logicNodeIds?.top;
      if (!membership || !nodeId) continue;
      const key = JSON.stringify([tile.tileType.split('/')[0], membership.channel]);
      let group = channels.get(key);
      if (!group) channels.set(key, (group = { send: [], receive: [], tileIds: [] }));
      group[membership.direction].push(nodeId);
      group.tileIds.push(tile.id);
    }
    for (const [channel, group] of channels) {
      if (!group.send.length || !group.receive.length) continue;
      // One OR bus bounds fan-in/fan-out edges to S+R, avoiding S×R links.
      const hub = this.graph.addNode('WIRE');
      hub.runtimeKey = JSON.stringify(['wireless', channel]);
      for (const sender of group.send) this.graph.addEdge(sender, hub.id);
      for (const receiver of group.receive) this.graph.addEdge(hub.id, receiver);
      this.wirelessHubs.set(channel, { nodeId: hub.id, tileIds: group.tileIds });
    }
  }

  private processTile(
    liftedOwners: ReadonlySet<string>,
    tile: TileInstance,
    queue: GraphBuildQueueItem[],
    visited: Set<string>,
    sourceNodeId?: NodeHandle,
    sourceTileId?: string,
    entryDirection?: Direction,
  ) {
    if (
      tile.meta.bundle ||
      tile.tileType === TileType.BundleInterface ||
      (tile.meta.partOfChip && liftedOwners.has(tile.meta.partOfChip))
    )
      return;
    const definition = tileDefinitionRegistry.get(tile.tileType);

    const visitKey = definition.getGraphBuildVisitKey
      ? definition.getGraphBuildVisitKey(tile, entryDirection)
      : `${tile.id}:${entryDirection ?? 'root'}`;

    const isFirstVisit = !visited.has(visitKey);
    if (isFirstVisit) {
      visited.add(visitKey);
    }

    if (definition.buildAndConnectNodes) {
      const newItems = definition.buildAndConnectNodes({
        graph: this.graph,
        tileManager: this.tileManager,
        connectionModel: this.buildConnections,
        instance: tile,
        sourceNodeId,
        sourceTileId,
        entryDirection,
      });

      if (isFirstVisit) {
        queue.push(...newItems);
      }
    }
  }

  private processChipInstances() {
    const placedTiles = new Map<string, TileInstance[]>();
    const owners = new Map<string, string>();
    this.nodeTiles.clear();
    for (const tile of this.tileManager.tiles) {
      for (const nodeId of Object.values(tile.logicNodeIds ?? {})) {
        const refs = this.nodeTiles.get(nodeId) ?? new Set<TileInstance>();
        refs.add(tile);
        this.nodeTiles.set(nodeId, refs);
      }
      if (tile.meta.partOfChip) {
        owners.set(tile.id, tile.meta.partOfChip);
        const group = placedTiles.get(tile.meta.partOfChip) ?? [];
        group.push(tile);
        placedTiles.set(tile.meta.partOfChip, group);
      }
    }
    this.removeIntraChipInterfaceEdges(owners);
    for (const chipInstance of this.chipInstances) {
      const tiles = placedTiles.get(chipInstance.id) ?? [];
      if (chipInstance.bundleWidth) this.embedLiftedChip(chipInstance, tiles);
      else this.embedChipInstanceLogic(chipInstance, tiles);
    }
    this.collapseWireToWireEdges();
    this.nodeTiles.clear();
  }

  /** Explicit lifting replicates actual authored logic and state, never just its footprint. */
  private embedLiftedChip(instance: IChipInstanceModel, tiles: TileInstance[]) {
    const chip = instance.chip;
    const width = instance.bundleWidth!;
    const error = validateChipLift(chip?.templateGraph ?? undefined, width);
    if (error || !chip?.templateGraph) {
      for (const tile of tiles)
        tile.bundleErrors.push(error ?? 'Authored logic unavailable');
      return;
    }
    const { graph, laneMaps, combinedMap } = cloneLiftedInstance(
      instance,
      chip.templateGraph,
      width,
    );
    const byLink = new Map(
      tiles.map((tile) => [
        (tile.state as InterfaceTileState<unknown>)?.connectedInterface?.interfaceLinkId,
        tile,
      ]),
    );
    for (const idMap of laneMaps) {
      for (const [linkId, templateSides] of chip.interfaceNodeMap) {
        const tile = byLink.get(linkId);
        if (!tile) continue;
        tile.laneNodeIds ??= {};
        for (const [side, templateId] of Object.entries(templateSides)) {
          const id = idMap.get(templateId);
          if (id) {
            tile.laneNodeIds[side as Side] ??= [];
            tile.laneNodeIds[side as Side]!.push(id);
          }
        }
      }
    }
    instance.logicGraph = graph;
    instance.templateToInstanceIdMap = combinedMap;
    this.graph.mergeFrom(graph);
  }

  /**
   * Embed a chip instance's logic graph into the parent graph by cloning the
   * chip's (already-stabilized) template graph, then wire-merging at interface
   * boundaries.
   */
  private embedChipInstanceLogic(
    chipInstance: IChipInstanceModel,
    placedTiles: TileInstance[],
  ) {
    const chip = chipInstance.chip;
    if (!chip) return;

    if (!chip.templateGraph || chip.templateGraph.nodes.size === 0) return;

    // Clone the already-stabilized template to get fresh unique node IDs while
    // preserving the stable initial state.
    const { graph: instanceGraph, idMap } = chip.templateGraph.clone();

    restoreInstanceRuntime(chipInstance, instanceGraph, idMap);
    if (chip.kind === 'bundle-adapter') {
      // Selected members are boundaries, not anonymous shared wire nodes.
      // Keep them distinct through the pre-lowering wire collapse; joining two
      // inner buses must not merge their containing A/B members prematurely.
      for (const node of instanceGraph.nodes.values()) {
        node.type = 'DIODE';
        node.definition = logicNodeRegistry.get('DIODE');
      }
    }

    // Update the chip instance's saved graph and ID map for the next rebuild.
    chipInstance.logicGraph = instanceGraph;
    chipInstance.templateToInstanceIdMap = idMap;

    this.graph.mergeFrom(instanceGraph);

    const instanceNodeIdSet = new Set(instanceGraph.nodes.keys());

    // Pre-build lookup: interfaceLinkId → placed tile (avoids O(T) tiles.find per link)
    const placedTileByLinkId = new Map<string, TileInstance>();
    for (const tile of placedTiles) {
      const state = tile.state as InterfaceTileState<unknown> | undefined;
      const linkId = state?.connectedInterface?.interfaceLinkId;
      if (linkId) {
        placedTileByLinkId.set(linkId, tile);
      }
    }

    // Wire-merge at interface boundaries
    for (const [linkId, link] of chip.interfaceLinkMap) {
      const templateNodeIds = chip.interfaceNodeMap.get(linkId);
      if (!templateNodeIds) continue;

      // Map template node IDs → cloned instance node IDs
      const instanceNodeIds: Partial<Record<Side, NodeHandle>> = {};
      for (const [side, templateNodeId] of Object.entries(templateNodeIds)) {
        if (templateNodeId) {
          instanceNodeIds[side as Side] = idMap.get(templateNodeId) ?? templateNodeId;
        }
      }

      const linkIdStr = linkId as InterfaceLinkInstanceId as string;
      const placedTile = placedTileByLinkId.get(linkIdStr);

      if (!placedTile?.logicNodeIds) continue;

      for (const side of Object.values(Side)) {
        const instanceNodeId = instanceNodeIds[side];
        const placedNodeId = placedTile.logicNodeIds[side];
        if (!instanceNodeId || !placedNodeId) continue;
        if (instanceNodeId === placedNodeId) continue;

        const instanceNode = this.graph.nodes.get(instanceNodeId);

        const liveInterfaceTile = link.tiles?.[CircuitType.ChipLogic];
        const carriedBinding =
          liveInterfaceTile && chip.templateTileBundleMap?.get(liveInterfaceTile.id);
        const carriesSelectors = carriedBinding?.laneSignalIds?.[side]
          ?.flat()
          .some(containsChipMemberIdentity);
        if (
          (this.previousIdentityMode || carriesSelectors) &&
          instanceNode?.bundlePath?.length &&
          !instanceNode.isBundleObserver
        ) {
          // A one-leaf bundle may bind directly to WIRE rather than an OR
          // observer. It still carries definition/member identity across wrappers.
          const liveTile = link.tiles[CircuitType.ChipLogic];
          const binding = liveTile && chip.templateTileBundleMap?.get(liveTile.id);
          const leaves = binding?.laneNodeIds?.[side];
          if (leaves?.length) {
            placedTile.laneNodeIds ??= {};
            placedTile.laneSignalIds ??= {};
            placedTile.laneSignalLabels ??= {};
            placedTile.laneNodeIds[side] = leaves.map((id) => idMap.get(id)!);
            placedTile.laneSignalIds[side] = binding?.laneSignalIds?.[side];
            placedTile.laneSignalLabels[side] = binding?.laneSignalLabels?.[side];
            placedTile.bundleSignalLabels = binding?.bundleSignalLabels;
          }
        }

        if (chip.kind === 'bundle-adapter') {
          // Keep authored labeled pins distinct from anonymous external wire
          // components. Their directional edges are matched by label during
          // physical bus lowering, never merged into all carried identities.
          this.graph.addEdge(instanceNodeId, placedNodeId);
          this.graph.addEdge(placedNodeId, instanceNodeId);
          placedTile.logicNodeIds[side] = instanceNodeId;
          const refs = this.nodeTiles.get(instanceNodeId) ?? new Set<TileInstance>();
          refs.add(placedTile);
          this.nodeTiles.set(instanceNodeId, refs);
        } else if (instanceNode?.isBundleObserver) {
          // An aggregate observation cannot drive or receive a whole bundle.
          // Preserve the child transport leaves and let physical cable lowering
          // match their labels at this placed boundary.
          const liveTile = link.tiles[CircuitType.ChipLogic];
          const binding = liveTile && chip.templateTileBundleMap?.get(liveTile.id);
          const leaves = binding?.laneNodeIds?.[side];
          if (leaves?.length) {
            placedTile.laneNodeIds ??= {};
            placedTile.laneSignalIds ??= {};
            placedTile.laneSignalLabels ??= {};
            placedTile.laneNodeIds[side] = leaves.map((id) => idMap.get(id)!);
            placedTile.laneSignalIds[side] = binding?.laneSignalIds?.[side];
            placedTile.laneSignalLabels[side] = binding?.laneSignalLabels?.[side];
            placedTile.bundleSignalLabels = binding?.bundleSignalLabels;
          }
        } else if (link.type === InterfaceType.MULTI) {
          // Each side is an independent net. Keep the internal wire object so
          // its settled signal and the instance's runtime snapshot stay live.
          // Bidirectional edges collapsed later could keep the new boundary
          // wire instead, resetting feedback and leaving stale instance state.
          this.mergeWireNodes(instanceNodeId, placedNodeId);
        } else if (instanceNode && instanceNode.type !== 'WIRE') {
          // Non-wire nodes (LEVER/BUTTON) output to the placed tile's wire node
          this.graph.addEdge(instanceNodeId, placedNodeId);
          // Point the placed tile's logicNodeIds to the internal node so
          // syncStateToNodes can reach it when the structure tile is toggled
          placedTile.logicNodeIds[side] = instanceNodeId;
          const refs = this.nodeTiles.get(instanceNodeId) ?? new Set<TileInstance>();
          refs.add(placedTile);
          this.nodeTiles.set(instanceNodeId, refs);
        } else {
          this.mergeWireNodes(instanceNodeId, placedNodeId);
        }
      }
    }

    if (chip.kind === 'bundle-adapter') {
      const templateNodes = [...chip.templateGraph.nodes.keys()];
      const lanes = templateNodes.map((id) => idMap.get(id)!).filter(Boolean);
      for (const tile of placedTiles) {
        if (tile.tileType !== TileType.BundleInterface) continue;
        tile.adapterPort = true;
        tile.adapterSignalIds = chip.adapterSignalIds;
        tile.adapterSignalLabels = chip.adapterSignalLabels;
        tile.adapterSignalAliases = chip.adapterSignalAliases;
        tile.effectiveIoSides = {
          [Side.LEFT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
          [Side.RIGHT]: { type: TileSideIOType.NONE, forceConnection: false },
          [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
          [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
        };
        tile.laneNodeIds = { [Side.LEFT]: lanes };
        for (const nodeId of lanes) {
          const refs = this.nodeTiles.get(nodeId) ?? new Set<TileInstance>();
          refs.add(tile);
          this.nodeTiles.set(nodeId, refs);
        }
        tile.laneSignalIds = {
          [Side.LEFT]: (chip.adapterSignalIds ?? []).map((id) => [id]),
        };
        tile.bundleSignalLabels = chip.adapterSignalLabels ?? {};
        tile.laneSignalLabels = {
          [Side.LEFT]: (chip.adapterSignalIds ?? []).map(
            (id) => chip.adapterSignalLabels?.[id] ?? '?',
          ),
        };
      }
    }

    this.fixCrossBoundaryEdgeDirections(chipInstance, instanceNodeIdSet, idMap);
  }

  /**
   * Fix edges at chip boundaries that have the wrong direction due to BFS order.
   * Uses the template graph to determine each interface's role (input vs output).
   */
  private fixCrossBoundaryEdgeDirections(
    chipInstance: IChipInstanceModel,
    instanceNodeIdSet: Set<NodeHandle>,
    idMap: Map<NodeHandle, NodeHandle>,
  ) {
    const chip = chipInstance.chip;
    if (!chip?.templateGraph) return;

    for (const [linkId, link] of chip.interfaceLinkMap) {
      // MultiInterface tiles have INPUT_OUTPUT on all sides — they're
      // bidirectional by design. Skip direction fixing entirely for these;
      // forcing a direction based on internal edges would break the signal path.
      if (link.type === InterfaceType.MULTI) continue;

      // Use templateGraph + interfaceNodeMap to determine input/output role
      const templateNodeIds = chip.interfaceNodeMap.get(linkId);
      if (!templateNodeIds) continue;

      // Iterate all sides to support MultiInterface tiles (4 independent nodes).
      // For SingleInterface tiles all sides share the same node — deduplicate.
      const processedNodeIds = new Set<NodeHandle>();
      for (const side of Object.values(Side)) {
        const templateNodeId = templateNodeIds[side];
        if (!templateNodeId) continue;

        const instanceNodeId = idMap.get(templateNodeId);
        if (!instanceNodeId || !this.graph.nodes.has(instanceNodeId)) continue;
        if (processedNodeIds.has(instanceNodeId)) continue;
        processedNodeIds.add(instanceNodeId);

        const isInput = chip.templateGraph
          .getEdgesFrom(templateNodeId)
          .some(
            (edge) => !chip.templateGraph!.nodes.get(edge.targetNodeId)?.isBundleObserver,
          );
        const isOutput = chip.templateGraph.getEdgesTo(templateNodeId).length > 0;

        if ((isInput && isOutput) || (!isInput && !isOutput)) continue;

        const outEdges = [...this.graph.getEdgesFrom(instanceNodeId)];
        const inEdges = [...this.graph.getEdgesTo(instanceNodeId)];

        if (isInput) {
          // Input: cross-boundary edges should point inward
          for (const edge of outEdges) {
            if (!instanceNodeIdSet.has(edge.targetNodeId)) {
              this.graph.removeEdge(edge.id);
              this.graph.addEdge(edge.targetNodeId, instanceNodeId, edge.meta);
            }
          }
        } else {
          // Output: cross-boundary edges should point outward
          for (const edge of inEdges) {
            if (!instanceNodeIdSet.has(edge.sourceNodeId)) {
              this.graph.removeEdge(edge.id);
              this.graph.addEdge(instanceNodeId, edge.sourceNodeId, edge.meta);
            }
          }
        }
      }
    }
  }

  /** Remove edges between placed interface tiles of the same chip instance. */
  private removeIntraChipInterfaceEdges(owners: Map<string, string>) {
    const validInstances = new Set(
      this.chipInstances.map((instance) => instance.id as string),
    );
    for (const edge of [...this.graph.edges.values()]) {
      const source = edge.meta?.sourceTileId && owners.get(edge.meta.sourceTileId);
      const target = edge.meta?.targetTileId && owners.get(edge.meta.targetTileId);
      if (source && source === target && validInstances.has(source))
        this.graph.removeEdge(edge.id);
    }
  }

  /**
   * Collapse all WIRE→WIRE edges by merging the connected wire nodes.
   * Worklist approach: seed with all WIRE→WIRE edges, process one at a time,
   * and after each merge check the surviving node's new edges for new candidates.
   * O(E + W*k) where k is average node degree, instead of O(W*E).
   */
  private collapseWireToWireEdges() {
    const worklist: import('./RuntimeHandles').EdgeHandle[] = [];
    for (const edge of this.graph.edges.values()) {
      const s = this.graph.nodes.get(edge.sourceNodeId);
      const t = this.graph.nodes.get(edge.targetNodeId);
      if (s?.type === 'WIRE' && t?.type === 'WIRE') {
        worklist.push(edge.id);
      }
    }

    // Safety cap: each merge removes exactly one node, so at most N merges.
    const maxMerges = this.graph.nodes.size;
    let merges = 0;
    let head = 0;

    while (head < worklist.length && merges < maxMerges) {
      const edgeId = worklist[head++];
      const edge = this.graph.edges.get(edgeId);
      if (!edge) continue;

      const s = this.graph.nodes.get(edge.sourceNodeId);
      const t = this.graph.nodes.get(edge.targetNodeId);
      if (!s || !t || s.type !== 'WIRE' || t.type !== 'WIRE') continue;

      // Keep authored scalar boundary objects so chip runtime snapshots remain live.
      const sourceBundle = s.rootRuntimeKey?.startsWith('["bundle",');
      const targetBundle = t.rootRuntimeKey?.startsWith('["bundle",');
      const keepId =
        sourceBundle && !targetBundle ? edge.targetNodeId : edge.sourceNodeId;
      const removeId =
        keepId === edge.sourceNodeId ? edge.targetNodeId : edge.sourceNodeId;
      if (sourceBundle && targetBundle) {
        const keep = this.graph.nodes.get(keepId)!;
        if (
          hasNativeRuntimeIdentity(s) &&
          hasNativeRuntimeIdentity(t) &&
          hasNativeRuntimeIdentity(keep)
        )
          keep.runtimeIdentity =
            s.rootRuntimeKey! < t.rootRuntimeKey! ? s.runtimeIdentity : t.runtimeIdentity;
        else keep.runtimeKey = [s.runtimeKey!, t.runtimeKey!].sort()[0];
      }
      this.mergeWireNodes(keepId, removeId);
      merges++;

      // Check surviving node's edges for new WIRE→WIRE candidates
      for (const e of this.graph.getEdgesFrom(keepId)) {
        const tn = this.graph.nodes.get(e.targetNodeId);
        if (tn?.type === 'WIRE') worklist.push(e.id);
      }
      for (const e of this.graph.getEdgesTo(keepId)) {
        const sn = this.graph.nodes.get(e.sourceNodeId);
        if (sn?.type === 'WIRE') worklist.push(e.id);
      }
    }
  }

  /** Merge two WIRE nodes into one, rewriting all edges and tile references. */
  private mergeWireNodes(keepNodeId: NodeHandle, removeNodeId: NodeHandle) {
    if (keepNodeId === removeNodeId) return;

    // Use edge indexes for O(k) lookup instead of O(E) full scan
    const touching = [
      ...this.graph.getEdgesFrom(removeNodeId),
      ...this.graph.getEdgesTo(removeNodeId),
    ].map((e) => ({
      sourceNodeId: e.sourceNodeId,
      targetNodeId: e.targetNodeId,
      meta: e.meta,
    }));

    const keep = this.graph.nodes.get(keepNodeId);
    const removed = this.graph.nodes.get(removeNodeId);
    if (keep && removed?.bundlePath?.length && !keep.bundlePath?.length) {
      keep.bundlePath = [...removed.bundlePath];
      keep.bundlePathLabels = removed.bundlePathLabels && [...removed.bundlePathLabels];
      keep.bundleTransportIdentities = removed.bundleTransportIdentities?.map((ids) => [
        ...ids,
      ]);
    }
    this.nodeAliases.set(removeNodeId, keepNodeId);
    this.graph.removeNode(removeNodeId);

    for (const edge of touching) {
      const newSource =
        edge.sourceNodeId === removeNodeId ? keepNodeId : edge.sourceNodeId;
      const newTarget =
        edge.targetNodeId === removeNodeId ? keepNodeId : edge.targetNodeId;
      if (newSource === newTarget) continue;
      this.graph.addEdge(newSource, newTarget, edge.meta);
    }

    const affectedTiles = this.nodeTiles.get(removeNodeId);
    const keptTiles = this.nodeTiles.get(keepNodeId) ?? new Set<TileInstance>();
    const rewrittenLaneArrays = new Set<NodeHandle[]>();
    for (const tile of affectedTiles ?? []) {
      // Shared lane arrays may already have been rewritten by an earlier tile.
      // Every indexed tile still references the surviving electrical node.
      keptTiles.add(tile);
      for (const laneIds of Object.values(tile.laneNodeIds ?? {})) {
        if (rewrittenLaneArrays.has(laneIds)) continue;
        rewrittenLaneArrays.add(laneIds);
        // Call the same live handlers as the native observable array proxy.
        // Unlike indexOf, this preserves the original loop's dynamic length and
        // needs no repeated dense-slot certification. Writes remain observable.
        const target = unobservedLaneTarget(laneIds);
        if (target) {
          for (let lane = 0; lane < target[$mobx].getArrayLength_(); lane++) {
            if (target[$mobx].get_(lane) === removeNodeId) {
              laneIds[lane] = keepNodeId;
              keptTiles.add(tile);
            }
          }
        } else {
          for (let lane = 0; lane < laneIds.length; lane++) {
            if (laneIds[lane] === removeNodeId) {
              laneIds[lane] = keepNodeId;
              keptTiles.add(tile);
            }
          }
        }
      }
      if (!tile.logicNodeIds) continue;
      for (const [side, nodeId] of Object.entries(tile.logicNodeIds) as [
        Side,
        NodeHandle,
      ][]) {
        if (nodeId === removeNodeId) {
          tile.logicNodeIds[side] = keepNodeId;
          keptTiles.add(tile);
        }
      }
    }
    this.nodeTiles.delete(removeNodeId);
    if (keptTiles.size) this.nodeTiles.set(keepNodeId, keptTiles);
  }
}
